import {
  useContext,
  useMemo,
  createContext,
  useEffect,
  ReactElement,
} from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  SearchIcon,
} from '@heroicons/react/outline';
import { CheckIcon } from '@heroicons/react/solid';
import { useState } from 'react';
import classNames from 'classnames';
import { useClickOutside } from '@mantine/hooks';

export type SelectOption = {
  label: string;
  value: any;
  disabled?: boolean;
  imageUrl?: string;
  className?: string;
};

export interface SelectProps {
  /** E2e targeting tag. */
  dataCy?: string;

  /** Array of items to show in dropdown */
  data?: SelectOption[];

  /** Disables dropdown button */
  disabled?: boolean;

  /** Watch for selection */
  onChange?: any;

  /** On Select Confirm */
  onConfirm?: any;

  /** Select Compound Components */
  children?: any;

  /** Array or Single value.  Depends on SelectMultiOptions or SelectOptions */
  selected?:
    | { label: string; value: any; disabled?: boolean }
    | { label: string; value: any; disabled?: boolean }[]
    | null;

  /** Boolean flag, determines whether chipped values are rendered or not */
  controlShouldRenderValue?: boolean;

  /** Replaces the placeholder of the input with provided prop */
  placeholder?: any;

  /** Reduces padding on the right of the select */
  tightPadding?: boolean;

  /** Overrides the internal value state, useful for reflecting external changes to selected items */
  value?: any;

  chevColor?: string;
}

export interface SelectDropdownProps {
  /** Overrides default w-52 tailwind class, add min-w, max-w, w-[x] here */
  classWidth?: string;

  /** Overrides default left position of dropdown. Choose right to make dropdown position right */
  position?: 'left' | 'right';

  /** Overrides default downwards opening of dropdown. Provide up to force dropdown to open upwards */
  openDirection?: 'down' | 'up';

  children?: any;
}

const SelectContext = createContext<any>({});
const useSelectContext = () => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error(
      `Select compound components cannot be rendered outside the Select component`,
    );
  }
  return context;
};

function SelectOptions({ className }: { className?: any }) {
  const {
    data,
    setOpen,
    onChange,
    innerSelected,
    setInnerSelected,
    searchTerm,
  } = useSelectContext();

  const handleClick = (item: any) => {
    if (onChange) onChange(item);
    if (setInnerSelected) setInnerSelected(item);
    setOpen(false);
  };

  return (
    <ul className={classNames('max-h-80 overflow-y-auto', className)}>
      {data
        .filter((item: any) => {
          return item.label
            .toLowerCase()
            .match(searchTerm && searchTerm.toLowerCase());
        })
        .map((item: any, idx: number) => {
          if (item.disabled) {
            return (
              <li
                className="cursor-not-allowed select-none text-sm relative py-2 pl-10 pr-4 h-10 flex items-center font-sans bg-disabled transition"
                key={idx}
                id="select-option-disabled"
              >
                <span>{item.label}</span>
              </li>
            );
          }
          return (
            <li
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleClick(item);
              }}
              onClick={(e) => {
                e.preventDefault();
                handleClick(item);
              }}
              tabIndex={0}
              className={classNames(
                'cursor-pointer select-none text-sm relative py-2 pr-4 h-10 flex items-center outline-none hover:bg-hover-secondary focus:bg-focus-secondary transition',
                { 'pl-10': !item.imageUrl },
                { 'pl-4': item.imageUrl },
                { [item.className]: item.className },
              )}
              key={idx}
              id="select-option"
            >
              {item.imageUrl ? (
                <span className="mr-4">
                  <img className="w-5 h-5" src={item.imageUrl} />
                </span>
              ) : null}
              <div className="flex">
                <span className="block truncate">{item.label}</span>
                {item.icon && <div className="ml-3">{item.icon}</div>}
              </div>
              {!item.imageUrl &&
              innerSelected &&
              innerSelected.value === item.value ? (
                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <CheckIcon className="w-4 h-4 stroke-1" />
                </span>
              ) : null}
            </li>
          );
        })}
    </ul>
  );
}

function SelectMultiOptions() {
  const {
    data,
    onChange,
    innerSelected,
    setInnerSelected,
    searchTerm,
    setIsMultiSelected,
  } = useSelectContext();
  useEffect(() => {
    setIsMultiSelected(true);
  }, []);
  const handleClick = (item: any) => {
    const index = innerSelected.findIndex((val: any) => {
      return val.value === item.value;
    });
    if (index === -1) {
      setInnerSelected([...innerSelected, item]);
      if (onChange) {
        onChange([...innerSelected, item]);
      }
    } else {
      const filteredItems = innerSelected.filter(
        (innerSelectedItem: any) => innerSelectedItem.value !== item.value,
      );
      setInnerSelected(filteredItems);
      if (onChange) {
        onChange(filteredItems);
      }
    }
  };

  return (
    <ul className="max-h-80 overflow-y-auto">
      {data
        .filter((item: any) => {
          return (
            item.label &&
            item.label
              .toLowerCase()
              .match(searchTerm && searchTerm.toLowerCase())
          );
        })
        .map((item: any, idx: number) => {
          if (item.disabled) {
            return (
              <li
                className="cursor-not-allowed select-none text-sm relative py-2 pl-10 pr-4 h-10 flex items-center outline-none font-sans bg-disabled transition"
                key={idx}
              >
                <span>{item.label}</span>
              </li>
            );
          }
          return (
            <li
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleClick(item);
              }}
              onClick={() => handleClick(item)}
              tabIndex={0}
              className={classNames(
                'cursor-pointer select-none text-sm relative py-2 pl-10 pr-4 h-10 flex items-center font-sans transition outline-none hover:bg-hover-secondary focus:bg-focus-secondary',
              )}
              key={idx}
            >
              {item.iconComponent}
              <span className="block truncate">{item.label}</span>
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <input
                  tabIndex={-1}
                  type="checkbox"
                  readOnly={true}
                  checked={
                    !!innerSelected.filter(
                      (innerSelectedItem: any) =>
                        item.value === innerSelectedItem.value,
                    ).length
                  }
                  className="custom-checkbox h-4 w-4 text-white focus:ring-black border border-gray-300 rounded-sm cursor-pointer relative appearance-none indeterminate:bg-gray-900 checked:bg-black"
                />
              </span>
            </li>
          );
        })}
    </ul>
  );
}

function SelectSearchBar() {
  const { setSearchTerm } = useSelectContext();

  const handleSearch = (val: string) => {
    setSearchTerm(val);
  };

  return (
    <div className="flex px-4 py-2 items-center relative">
      <SearchIcon className="stroke-1 h-4 w-4 mr-1 absolute left-4" />
      <input
        className="outline-none pl-5 placeholder:text-neutral-400 font-sans w-full"
        type="text"
        placeholder="Search"
        onChange={(e: any) => handleSearch(e.target.value)}
      />
    </div>
  );
}

function SelectAllButton() {
  const { data, onChange, setInnerSelected } = useSelectContext();

  const [isSelectedAll, setSelectedAll] = useState<boolean>();

  const handleSelectAll = () => {
    if (isSelectedAll) {
      setInnerSelected([]);
      if (onChange) {
        onChange([]);
      }
    } else {
      setInnerSelected(data);
      if (onChange) {
        onChange(data);
      }
    }
    setSelectedAll(!isSelectedAll);
  };

  return (
    <div>
      <button
        onClick={handleSelectAll}
        className="border-none bg-transparent m-0 py-3 px-4 w-full text-left text-sm text-system-message outline-none focus:bg-focus-secondary"
      >
        {isSelectedAll ? 'Deselect All' : 'Select All'}
      </button>
    </div>
  );
}

function DeselectAllButton() {
  const { onChange, setInnerSelected, innerSelected } = useSelectContext();
  const isDisabled = innerSelected.length === 0;
  const handleDeselectAll = () => {
    setInnerSelected([]);
    if (onChange) {
      onChange([]);
    }
  };
  return (
    <div>
      <button
        onClick={handleDeselectAll}
        className="border-none bg-transparent m-0 py-3 px-4 w-full text-left text-sm text-system-message outline-none focus:bg-focus-secondary disabled:text-neutral-gray-500"
        disabled={isDisabled}
      >
        Deselect All
      </button>
    </div>
  );
}

function SelectConfirm() {
  const { onConfirm, setOpen, innerSelected } = useSelectContext();
  const handleCancel = () => {
    setOpen(false);
  };
  const handleConfirm = () => {
    onConfirm(innerSelected);
    setOpen(false);
  };
  return (
    <div className="h-12 flex justify-end items-center bg-white space-x-2 px-4">
      <button
        className="text-sm outline-none hover:bg-hover-secondary focus:bg-focus-secondary px-4 py-2 rounded-full"
        data-cy="select-cancel"
        onClick={handleCancel}
      >
        Cancel
      </button>
      <button
        className="text-sm text-violet-400 outline-none hover:bg-hover-secondary focus:bg-focus-secondary px-4 py-2 rounded-full"
        data-cy="select-confirm"
        onClick={handleConfirm}
      >
        Confirm
      </button>
    </div>
  );
}

function SelectDropdown({
  classWidth = 'w-52',
  position = 'left',
  openDirection = 'down',
  children,
}: SelectDropdownProps) {
  const { open } = useSelectContext();

  return (
    <div
      data-cy={'select-dropdown'}
      key={'select-dropdown'}
      className={classNames(
        'absolute z-30 min-w-full mt-2 origin-top-right bg-white rounded-sm shadow-elevation-low',
        {
          hidden: !open,
          block: open,
        },
        classWidth,
        {
          'left-0': position === 'left',
          'right-0': position === 'right',
          'bottom-full': openDirection === 'up',
        },
      )}
    >
      {children}
    </div>
  );
}

function SelectButton(props: {
  label?: string;
  dataCy?: string;
  type?: 'primary' | 'secondary' | 'text' | 'lite';
  size?: 'sm' | 'md' | 'lg';
  labelClassName?: string;
  getLabel?: (selected: any) => string | ReactElement;
  children?: any;
  onClick?: (open: boolean) => void;
}) {
  const { open, setOpen, chevPosition, selected, isMultiSelected, disabled } =
    useSelectContext();

  const getLabel = () => {
    if (isMultiSelected) {
      return props.getLabel
        ? props.getLabel(props.label)
        : selected?.length
        ? `${selected.length} Selected`
        : props.label;
    } else {
      return selected
        ? props.getLabel
          ? props.getLabel(selected)
          : selected.label
        : props.label;
    }
  };

  let buttonStyles;
  switch (props.type) {
    case 'primary':
      buttonStyles =
        'hover:bg-hover-primary focus:bg-focus-primary text-white rounded-full border border-deep-space-200 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-neutral-gray-100';
      break;
    case 'secondary':
      buttonStyles =
        'hover:bg-hover-secondary focus:bg-focus-secondary text-black rounded-full border border-deep-space-200 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-neutral-gray-100';
      break;
    case 'lite':
      buttonStyles =
        'hover:bg-hover-secondary focus:bg-focus-secondary text-black rounded-full border border-neurtal-gray-200 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-neutral-gray-100';
      break;
    case 'text':
    default:
      buttonStyles =
        'hover:bg-hover-secondary focus:bg-focus-secondary text-black disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-neutral-gray-100';
      break;
  }

  let sizeStyles;
  switch (props.size) {
    case 'sm':
      sizeStyles = 'py-1 px-3';
      break;
    case 'md':
      sizeStyles = '';
      break;
    case 'lg':
      sizeStyles = '';
      break;
    default:
      sizeStyles = 'py-2 px-4';
      break;
  }

  const handleOnClick = () => {
    setOpen(!open);
    if (props.onClick) {
      props.onClick(!open);
    }
  };

  return (
    <button
      className={classNames(
        'flex outline-none rounded-full cursor-pointer justify-between w-full text-sm items-center',
        buttonStyles,
        sizeStyles,
      )}
      data-cy={props.dataCy}
      onClick={() => handleOnClick()}
      disabled={disabled}
      type="button"
      title={props.label}
    >
      {props.children ? (
        props.children
      ) : (
        <>
          <span
            className={classNames('mr-1 tabular-nums', props.labelClassName)}
          >
            {getLabel()}
          </span>{' '}
          {chevPosition}
        </>
      )}
    </button>
  );
}

function SelectTypeahead(props: {
  label: string;
  dataCy?: string;
  name?: string;
}) {
  const { open, setOpen, innerSelected, setSearchTerm } = useSelectContext();

  const [value, setValue] = useState(innerSelected || null);

  useEffect(() => {
    setValue(innerSelected);
  }, [innerSelected]);

  useEffect(() => {
    if (!open) {
      setValue(innerSelected);
    }
  }, [open]);

  const handleSearch = (target: any) => {
    setValue(target);
    setSearchTerm(target.value);
  };

  return (
    <input
      className="flex items-center border border-neutral-300 bg-white focus:border-black outline-none rounded-md py-2 px-6 cursor-pointer w-full text-sm"
      data-cy={props.dataCy}
      onClick={(e) => {
        e.preventDefault();
        setOpen(!open);
      }}
      onChange={(e) => handleSearch(e.target)}
      value={value?.label}
      autoComplete="off"
      name={props.name}
      id={`search-${props.name}`}
    ></input>
  );
}

function SelectInput(props: {
  label: string;
  dataCy?: string;
  size?: string;
  className?: string;
  labelRender?: any;
}) {
  const {
    open,
    setOpen,
    chevPosition,
    innerSelected,
    isMultiSelected,
    selected,
    disabled,
  } = useSelectContext();

  const getLabel = () => {
    if (isMultiSelected) {
      return innerSelected ? `${innerSelected.length} Selected` : props.label;
    } else {
      return innerSelected ? innerSelected.label : props.label;
    }
  };

  let sizeStyles;
  switch (props.size) {
    case 'sm':
      sizeStyles = 'p-2';
      break;
    default:
      sizeStyles = 'py-2 px-6';
      break;
  }

  return (
    <button
      className={classNames(
        'flex items-center border border-neutral-300 bg-white focus:border-black outline-none rounded-md disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-300 cursor-pointer w-full text-sm',
        sizeStyles,
        props.className,
      )}
      data-cy={props.dataCy}
      onClick={(e) => {
        e.preventDefault();
        setOpen(!open);
      }}
      disabled={disabled}
    >
      {selected?.imageUrl ? (
        <span className="mr-4">
          <img src={selected.imageUrl} className="w-5 h-5" />
        </span>
      ) : null}
      <span className="mr-3">
        {props.labelRender ? props.labelRender({ selected }) : getLabel()}
      </span>
      <span className="flex-1 flex justify-end">{chevPosition}</span>
    </button>
  );
}

function SelectIconButton(props: { dataCy?: string; icon: ReactElement }) {
  const { open, setOpen, disabled } = useSelectContext();

  return (
    <button
      className="flex rounded-full cursor-pointer w-full items-center"
      data-cy={props.dataCy}
      onClick={(e) => {
        e.preventDefault();
        setOpen(!open);
      }}
      disabled={disabled}
      type="button"
    >
      {props.icon}
    </button>
  );
}

function Select({
  data,
  selected,
  onChange,
  onConfirm,
  children,
  disabled,
  tightPadding,
  dataCy,
  chevColor,
}: SelectProps) {
  const [chevPosition, setChevPosition] = useState(
    <ChevronDownIcon
      className={classNames('w-6 h-6 shrink-0 stroke-1', chevColor, {
        '-mr-3': tightPadding,
      })}
    />,
  );
  const [searchTerm, setSearchTerm] = useState();
  const [open, setOpen] = useState(false);
  const [innerSelected, setInnerSelected] = useState(selected);
  const [isMultiSelected, setIsMultiSelected] = useState(false);
  const onDropDownOpen = () => {
    setChevPosition(
      <ChevronUpIcon
        className={classNames('w-6 h-6 shrink-0 stroke-1', chevColor, {
          '-mr-3': tightPadding,
        })}
      />,
    );
  };

  const onDropDownClose = () => {
    setChevPosition(
      <ChevronDownIcon
        className={classNames('w-6 h-6 shrink-0 stroke-1', chevColor, {
          '-mr-3': tightPadding,
        })}
      />,
    );
  };

  const ref = useClickOutside(() => setOpen(false));

  useEffect(() => {
    if (open) {
      onDropDownOpen();
    } else {
      onDropDownClose();

      // Reset selected state if user did not confirm
      if (onConfirm) {
        setInnerSelected(selected);
      }
    }
  }, [open]);

  useEffect(() => {
    setInnerSelected(selected);
  }, [selected]);

  const value = useMemo(
    () => ({
      searchTerm,
      setSearchTerm,
      setChevPosition,
      chevPosition,
      open,
      setOpen,
      onDropDownOpen,
      onDropDownClose,
      data,
      innerSelected,
      setInnerSelected,
      onChange,
      onConfirm,
      selected,
      setIsMultiSelected,
      isMultiSelected,
      disabled,
    }),
    [
      searchTerm,
      setSearchTerm,
      chevPosition,
      open,
      setChevPosition,
      setOpen,
      onDropDownOpen,
      onDropDownClose,
      data,
      innerSelected,
      setInnerSelected,
      onChange,
      onConfirm,
      selected,
      disabled,
    ],
  );

  return (
    <div data-cy={dataCy} className="relative" ref={ref}>
      <SelectContext.Provider value={value}>{children}</SelectContext.Provider>
    </div>
  );
}

Select.displayName = 'Select';
Select.filePath = 'libs/shared/ui-kit/src/lib/select/select.tsx';

export {
  SelectDropdown,
  SelectOptions,
  SelectSearchBar,
  SelectAllButton,
  SelectButton,
  SelectConfirm,
  SelectMultiOptions,
  SelectInput,
  SelectIconButton,
  Select,
  SelectTypeahead,
  DeselectAllButton,
};
